import api from '@/lib/api/axios'

/**
 * Enrolment codes (MFA build plan, Stage 2): the one-time code that vouches for a user's first
 * sign-in device. Mirrors backend lib/enrolment-codes.js publicCode - the code itself is in the
 * answer to issuing it, once, and nowhere else.
 */
export type EnrolmentCodeStatus = 'pending_approval' | 'active' | 'used' | 'revoked' | 'expired'
interface Person { id: number; name: string | null; email: string }
export interface EnrolmentCode {
    id: number
    userId: number
    purpose: 'first_device' | 'reset' | 'handover' | 'mobilisation'
    status: EnrolmentCodeStatus
    deliveredVia: 'shown' | 'sms'
    issuedBy: number | null
    issuedAt: string
    reason: string | null
    needsApproval: boolean
    approvedBy: number | null
    approvedAt: string | null
    expiresAt: string
    attempts: number
    usedAt: string | null
    revokedAt: string | null
    revokeReason: string | null
    user: Person | null
    issuedByUser: Person | null
    approvedByUser: Person | null
}

interface Wrapped<T> { success: boolean; data: T }

export const enrolmentApi = {
    /** Mid sign-in: offer the code, on the enrolment-code token (not a session). */
    redeem: async (enrolmentToken: string, code: string, factor?: 'totp' | 'webauthn') =>
        (await api.post<Wrapped<{ mfaSetupToken: string; factor: 'totp' | 'webauthn' }>>(
            '/auth/enrolment/redeem', { code, ...(factor ? { factor } : {}) }, { stepUpToken: enrolmentToken } as Record<string, unknown>,
        )).data.data,

    listForUser: async (userId: number) => (await api.get<Wrapped<{ codes: EnrolmentCode[] }>>(`/api/enrolment-codes/user/${userId}`)).data.data.codes,
    pending: async () => (await api.get<Wrapped<{ codes: EnrolmentCode[] }>>('/api/enrolment-codes/pending')).data.data.codes,
    issue: async (body: { userId: number; reason: string; hours?: number }) =>
        (await api.post<Wrapped<{ code: string; record: EnrolmentCode }>>('/api/enrolment-codes', body)).data.data,
    approve: async (id: number) => (await api.post<Wrapped<{ record: EnrolmentCode }>>(`/api/enrolment-codes/${id}/approve`)).data.data.record,
    revoke: async (id: number, reason: string) =>
        (await api.post<Wrapped<{ record: EnrolmentCode }>>(`/api/enrolment-codes/${id}/revoke`, { reason })).data.data.record,
}

export const enrolmentKeys = {
    forUser: (userId: number) => ['enrolment-codes', 'user', userId] as const,
    pending: ['enrolment-codes', 'pending'] as const,
}

/** The estate-wide rule that every user needs a second factor, and who is not ready for it (Stage 3). */
export interface MfaPolicy {
    mandatory: boolean
    /** High-risk actions need a fresh proof of a passkey, security key or the phone app (Stage 7). */
    stepUpEnforced: boolean
    activeUsers: number
    withDevice: number
    withoutDeviceCount: number
    withoutDevice: Array<{ id: number; name: string | null; email: string; lastLogin: string | null; roles: string[]; code: 'active' | 'pending_approval' | null }>
}
export const mfaPolicyApi = {
    get: async () => (await api.get<Wrapped<MfaPolicy>>('/api/mfa-policy')).data.data,
    set: async (mandatory: boolean, reason: string) => (await api.put<Wrapped<MfaPolicy>>('/api/mfa-policy', { mandatory, reason })).data.data,
    setStepUp: async (enforced: boolean, reason: string) => (await api.put<Wrapped<{ stepUpEnforced: boolean }>>('/api/mfa-policy/step-up', { enforced, reason })).data.data,
}
export const mfaPolicyKey = ['mfa-policy'] as const
